// Sprint PKG-VIZ A1 (#792) — node-type registry.
//
// NAMED_NODE_TYPES is the single source of truth for "which entityType
// has a dedicated component." PKGCanvas derives its fallback predicate
// from this map's keys (adjudication V3: `!(entityType in NAMED_NODE_TYPES)`)
// rather than a separately-maintained exclusion list, so the two
// structurally cannot drift apart.

import type { ComponentType } from 'react';
import type { NodeProps, NodeTypes } from '@xyflow/react';
import type { PKGNodeEntityType } from '@blueprint/shared';
import { DecisionNode } from './DecisionNode.tsx';
import { RiskNode } from './RiskNode.tsx';
import { RequirementNode } from './RequirementNode.tsx';
import { StoryNode } from './StoryNode.tsx';
import { MeetingNode } from './MeetingNode.tsx';
import { ArtefactNode } from './ArtefactNode.tsx';
import { FallbackNode } from './FallbackNode.tsx';
import type { PKGNodeComponentProps } from './DecisionNode.tsx';

export type { PKGNodeComponentProps } from './DecisionNode.tsx';

export const FALLBACK_NODE_TYPE = 'fallback' as const;

// Keyed on the real pkg_node_entity_type values these components
// actually render (adjudication Q2/Q3) — NOT the full 10-value enum.
export const NAMED_NODE_TYPES: Partial<Record<PKGNodeEntityType, ComponentType<PKGNodeComponentProps>>> = {
  decision: DecisionNode,
  risk: RiskNode,
  requirement: RequirementNode,
  user_story: StoryNode,
  meeting: MeetingNode,
  artefact: ArtefactNode,
};

/** Passed straight to ReactFlow's `nodeTypes` prop. */
export const nodeTypes: NodeTypes = {
  ...(NAMED_NODE_TYPES as Record<string, ComponentType<NodeProps>>),
  [FALLBACK_NODE_TYPE]: FallbackNode as unknown as ComponentType<NodeProps>,
};

/**
 * Resolves the React Flow `type` string for a given entityType — the
 * name of a NAMED_NODE_TYPES key if one exists, else the fallback key.
 * This is the fallback-resolution logic V1 locks inside PKGCanvas
 * (via this shared helper), not pushed onto consumers.
 *
 * R2 fix (F1): must check OWN properties only. `entityType in
 * NAMED_NODE_TYPES` also matches inherited Object.prototype keys
 * ('constructor' in {} is true) — a malformed/untrusted entityType of
 * 'constructor' or 'toString' would skip the fallback entirely and
 * resolve to Object.prototype's own value at that key, which React
 * Flow then can't mount as a component. hasOwnProperty.call bypasses
 * the prototype chain.
 */
export function resolveNodeType(entityType: PKGNodeEntityType): string {
  return Object.prototype.hasOwnProperty.call(NAMED_NODE_TYPES, entityType)
    ? entityType
    : FALLBACK_NODE_TYPE;
}
